import { useState } from 'react';
import { Hash, Check } from 'lucide-react';

export interface CopyNumberProps {
  /** Numer do skopiowania i wyświetlenia — np. "1", "15.3", "3.2.1". */
  number: string;
  /** Wariant wyglądu: `chip` (główny, obok nagłówków), `inline` (mniejszy, obok podtytułów). */
  variant?: 'chip' | 'inline';
  className?: string;
}

/**
 * Chip pokazujący numer sekcji/elementu z kliknięciem kopiującym go do schowka.
 * User klika → numer leci do clipboardu → pokazuje się "Skopiowano" przez 1.5s.
 * Główny use-case: user mówi "nie podoba mi się 15.3" zamiast opisywać element.
 */
export function CopyNumber({ number, variant = 'chip', className }: CopyNumberProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(number);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback: zaznacz numer, user skopiuje Ctrl+C (rzadki przypadek)
    }
  };

  const baseChip =
    'inline-flex items-center gap-1 font-mono font-semibold rounded-md border transition-all cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40';
  const sizeChip =
    variant === 'chip'
      ? 'px-2 py-0.5 text-xs'
      : 'px-1.5 py-0.5 text-[10px]';
  const stateChip = copied
    ? 'bg-success/15 text-success border-success/40'
    : 'bg-surface-hover/60 text-text-muted border-border-subtle hover:bg-accent-primary/10 hover:text-accent-primary hover:border-accent-primary/30';

  const Icon = copied ? Check : Hash;
  const iconSize = variant === 'chip' ? 'w-3 h-3' : 'w-2.5 h-2.5';

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Skopiowano!' : `Skopiuj ${number}`}
      aria-label={`Skopiuj numer ${number}`}
      className={[baseChip, sizeChip, stateChip, className].filter(Boolean).join(' ')}
    >
      <Icon className={iconSize} aria-hidden="true" />
      <span>{copied ? 'Skopiowano' : number}</span>
    </button>
  );
}
